import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
    fetchAllowedMenus,
    fetchLeadData,
    fetchLeadDataById,
    fetchLocationData,
    GetLeadLog,
    LeadLgoList,
} from "../Services/APIService";
import { useNavigate, useLocation } from "react-router-dom";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import OverlayLoader from "../Components/OverlayLoader";

// Interfaces
interface Lead {
    leadId: number;
    firstName: string;
    lastName: string;
    gender: string;
    dob: string;
    dom: string;
    mobile: string;
    email: string;
    address: string;
    stateId: number;
    districtId: number;
    cityId: number;
    zipCode: number | string;
    sourceId: number;
    subSourceId: number;
    currentStatus: number;
    incomeFrom: number | string;
    incomeTo: number | string;
    initialAssignedUser: number;
    currentAssignedUser: number;
}
interface ShowCardLeadListProps {
    isShowFollowupIcon?: boolean; // optional prop
}
interface MenuItem {
    menuID: number;
    menuName: string;
    menuUrl: string;
    icon: string;
    subMenuList?: [];
}

interface Location {
    locationID: number;
    locationName: string;
}

const ReconnectLeads: React.FC<ShowCardLeadListProps> = ({
    isShowFollowupIcon,
}) => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [Leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [searchText, setSearchText] = useState<string>("");
    const [locations, setLocations] = useState<Location[]>([]);

    const navigate = useNavigate();
    const location = useLocation();
    const filterType = location.state?.filterType || "all";
    const isShowFollowup = location.state?.isShowFollowupIcon ?? false;

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    useEffect(() => {
        const getMenus = async () => {
            setIsLoading(true);
            try {
                const menuID = location.state?.menuID;
                const previousMenuID = localStorage.getItem("activeMenuID");
                if (menuID !== previousMenuID || menuID === null) {
                    localStorage.setItem("activeMenuID", menuID);
                } else {
                    localStorage.removeItem("activeMenuID");
                }
            } catch (error) {
                console.error("Error fetching menus:", error);
            } finally {
                setIsLoading(false);
            }
        };

        getMenus();
    }, []);

    useEffect(() => {
        const fetchLeads = async (): Promise<void> => {
            try {
                const usersData = await fetchLeadData();

                if (filterType === "reconnect") {
                    const logs: LeadLgoList[] = await GetLeadLog();

                    // Apply: STATUS_ID > 1 OR FOLLOWUP_CONDUCTED > 0
                    const nextActionrequiredLeadIds = logs
                        .filter(
                            (log) =>
                                log.eventTypeId === 6
                        )
                        .map((log) => log.leadId)
                        .filter((value, index, self) => self.indexOf(value) === index);

                    

                    // Filter leads based on new condition
                    const filteredLeads = usersData.filter((lead) =>
                        nextActionrequiredLeadIds.includes(lead.leadId)
                    );

                    setLeads(filteredLeads);
                } else {
                    setLeads(usersData);
                }

                setLoading(false);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
                setLoading(false);
            }
        };

        fetchLeads();
    }, [filterType]);


    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await fetchLocationData();
                if (!response) throw new Error("Failed to fetch location data");
                setLocations(response);
            } catch (error) {
                console.error("Error fetching locations:", error);
            }
        };
        fetchLocations();
    }, []);

    const filteredLeads = Leads.filter((lead) => {
        const text = searchText.toLowerCase();
        return (
            lead.firstName?.toLowerCase().includes(text) ||
            lead.lastName?.toLowerCase().includes(text) ||
            lead.email?.toLowerCase().includes(text) ||
            lead.mobile?.toLowerCase().includes(text)
        );
    });

    const handleEdit = async (LeadId: number) => {
        try {
            const response = await fetchLeadDataById(LeadId);
            if (response) {
                navigate("/editLead", {
                    state: { LeadData: response },
                });
            } else {
                throw new Error("No lead data found");
            }
        } catch (error) {
            console.error("Error fetching lead edit data:", error);
            alert("Failed to fetch lead data for editing");
        }
    };

    const LeadDetailsRedirection = (LeadId: number) => {
        navigate("/LeadDashboard", { state: { leadId: LeadId } });
    };

    const createusertaskRedirection = (LeadId: number) => {
        navigate("/UserTaskView", {
            state: { LeadId, openAddModal: true },
        });
    };

    const handleRowSelected = (state: { selectedRows: Lead[] }) => {
        console.log("Selected rows:", state.selectedRows);
    };
    const [toggleCleared, setToggleCleared] = useState(false);
    const columns = [
        {
            name: "Lead Name",
            selector: (row: Lead) => row.firstName,
            sortable: true,
            cell: (row: Lead) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div
                        style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            backgroundColor: "rgb(167 133 144)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "10px",
                            fontWeight: "bold",
                        }}
                    >
                        {row.firstName.charAt(0).toUpperCase()}
                    </div>
                    <span style={{ fontWeight: "500", color: "#33475b" }}>
                        {row.firstName + " " + (row.lastName ?? "")}
                    </span>
                </div>
            ),
            width: "200px",
        },

        {
            name: "Email",
            selector: (row: Lead) => row.email,
            sortable: true,
            width: "250px",
        },
        {
            name: "Mobile",
            selector: (row: Lead) => `+91 ${row.mobile?.slice(-10)}`,
            sortable: true,
        },

        {
            name: "Action",
            cell: (row: Lead) => (
                <>
                    <div className="action-cell">
                        <button className="action-button eidt-button">
                            <i
                                className="bi bi-eye-fill"
                                style={{ cursor: "pointer" }}
                                title="Lead Details"
                                onClick={() => LeadDetailsRedirection(row.leadId)}
                            ></i>
                        </button>
                        {isShowFollowup && (
                            <button className="action-button eidt-button">
                                {" "}
                                <i
                                    className="bi bi-telephone-fill"
                                    style={{ cursor: "pointer", marginLeft: "10px" }}
                                    title="Follow Up"
                                    onClick={() => createusertaskRedirection(row.leadId)} // Replace with actual follow-up logic
                                ></i>
                            </button>
                        )}
                    </div>
                </>
            ),
        },
    ];

    // if (loading)
    //   return <div className="p-4 text-center">Loading Leads data...</div>;
    // if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

    return (
        <div className="wrapper">
            {!isSidebarCollapsed && <CollapsibleSidebar />}
            <div className="main">
                <Navbar onToggleSidebar={toggleSidebar} />
                <div className="main-content-div">
                    <div className="main-header-div"></div>
                    <main className="content mainContent">
                        <div className="container-fluid p-0">
                            <div className="row">
                                <div className="col-12 col-lg-12">
                                    <div className="card">
                                        <div className="card-header carHeaderWithBtn">
                                            <div className="card-title mb-0">
                                                {filterType === "reconnect"
                                                    ? "Reinquire Leads"
                                                    : "Leads List"}
                                            </div>
                                        </div>
                                        <div className="card-body p-0">
                                            <DataTable
                                                columns={columns}
                                                data={Leads}
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                                highlightOnHover
                                                striped
                                                responsive
                                                noDataComponent={
                                                    <div className="text-center py-4">
                                                        <p className="text-muted mb-0">
                                                            No media sources found.
                                                        </p>
                                                    </div>
                                                }
                                                selectableRows
                                                selectableRowsComponentProps={{
                                                    className: "custom-checkbox",
                                                }}
                                                onSelectedRowsChange={handleRowSelected}
                                                clearSelectedRows={toggleCleared}
                                                fixedHeader
                                                fixedHeaderScrollHeight="400px"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
                <Footer />
            </div>
            <OverlayLoader
                loading={loading}
                message="Loading Lead Data"
                submessage="This might take a moment..."
            />
        </div>
    );
};

export default ReconnectLeads;
